/**
 * Kwolly – Text With Icons
 * Handles scroll-triggered reveal animation via IntersectionObserver.
 * Manages Theme Editor lifecycle events for live preview correctness.
 */

(function () {
  'use strict';

  /**
   * Controls the reveal animation for a single .kwolly-twi section.
   * Uses IntersectionObserver to add .is-visible when items enter the viewport.
   */
  class KwollyTextWithIcons {
    /**
     * @param {Element} section – the .kwolly-twi root element
     */
    constructor(section) {
      this.section = section;
      this.items = Array.from(
        section.querySelectorAll('.kwolly-twi__item')
      );
      this.observer = null;
      this._init();
    }

    _init() {
      // If IntersectionObserver is unavailable (old browser / SSR), show all immediately
      if (typeof IntersectionObserver === 'undefined') {
        this.showAll();
        return;
      }

      // If prefers-reduced-motion, skip animation — items remain visible via CSS default
      const prefersReduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      if (prefersReduced) {
        return;
      }

      // Gate the hide/reveal animation — items are visible by default until this class is added
      this.section.classList.add('kwolly-twi--js-ready');

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -40px 0px'
        }
      );

      this.items.forEach((item) => this.observer.observe(item));
    }

    /** Force all items visible — used in Theme Editor and for reduced-motion / no-JS fallback */
    showAll() {
      this.items.forEach((item) => item.classList.add('is-visible'));
    }

    /** Clean up observer when section is removed from the editor */
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  // ─── Initialisation ──────────────────────────────────────────────────────

  /**
   * Initialise all .kwolly-twi sections found within a given root.
   * Stores the instance on the element to enable later cleanup.
   *
   * @param {Document|Element} root
   */
  function init(root) {
    root.querySelectorAll('.kwolly-twi').forEach((section) => {
      // Guard: don't double-init
      if (section._kwollyTWI) return;
      section._kwollyTWI = new KwollyTextWithIcons(section);
    });
  }

  // Initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // ─── Shopify Theme Editor events ─────────────────────────────────────────

  /**
   * shopify:section:load fires when a section is added or re-rendered in the editor.
   * Re-initialise to pick up any new blocks.
   */
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.kwolly-twi');
    if (!section) return;

    // Destroy any stale instance first
    if (section._kwollyTWI) {
      section._kwollyTWI.destroy();
      delete section._kwollyTWI;
    }

    // In the editor, show items without animation so merchants see content immediately
    section._kwollyTWI = new KwollyTextWithIcons(section);
    section._kwollyTWI.showAll();
  });

  /**
   * shopify:section:unload fires when a section is removed from the editor.
   * Clean up to prevent memory leaks.
   */
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.kwolly-twi');
    if (!section) return;

    if (section._kwollyTWI) {
      section._kwollyTWI.destroy();
      delete section._kwollyTWI;
    }
  });

  /**
   * shopify:block:select fires when a merchant clicks a block in the editor.
   * Ensure the selected item is visible (it might still be hidden if animation hasn't run).
   */
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target;
    if (item && item.classList.contains('kwolly-twi__item')) {
      item.classList.add('is-visible');
    }
  });

  /**
   * shopify:block:deselect fires when a merchant deselects a block.
   * No action needed — keep item visible to avoid layout jumps.
   */
})();
